import { useState, useEffect, useCallback, useMemo } from 'react';
import { collection, onSnapshot, query, orderBy, limit } from 'firebase/firestore';
import { db } from '../../../firebase';
import ToastNotification from '../../../components/ToastNotification';
import styles from './AuditLogSection.module.css';

const MODULE_STYLE = {
  Orders:       styles.modOrders,
  Ordering:     styles.modOrdering,
  Inventory:    styles.modInventory,
  Transactions: styles.modTransactions,
  Reports:      styles.modReports,
};

function ModuleBadge({ module }) {
  return <span className={`${styles.modBadge} ${MODULE_STYLE[module] || ''}`}>{module}</span>;
}

function formatTimestamp(ts) {
  if (!ts) return '—';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleString('en-PH', { hour12: false, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
}

function exportToXML(data, filename) {
  if (!data.length) return;
  const headers = Object.keys(data[0]);
  const rows = data.map(row =>
    `  <Row>${headers.map(h => `<Cell><Data ss:Type="String">${String(row[h] ?? '').replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;')}</Data></Cell>`).join('')}</Row>`
  ).join('\n');
  const xml = `<?xml version="1.0"?>\n<?mso-application progid="Excel.Sheet"?>\n<Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet" xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet">\n<Worksheet ss:Name="Sheet1"><Table>\n  <Row>${headers.map(h => `<Cell><Data ss:Type="String">${h}</Data></Cell>`).join('')}</Row>\n${rows}\n</Table></Worksheet></Workbook>`;
  const blob = new Blob([xml], { type: 'application/vnd.ms-excel' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

export default function AuditLogSection() {
  const [logs, setLogs]             = useState([]);
  const [dateFilter, setDateFilter] = useState('');
  const [toast, setToast]           = useState(null);
  const dismissToast                = useCallback(() => setToast(null), []);

  useEffect(() => {
    const q = query(collection(db, 'auditLogs'), orderBy('timestamp', 'desc'), limit(100));
    const unsub = onSnapshot(q, snap => {
      setLogs(snap.docs.map(d => ({ _docId: d.id, ...d.data() })));
    });
    return unsub;
  }, []);

  const filtered = useMemo(() => {
    if (!dateFilter) return logs;
    return logs.filter(log => {
      if (!log.timestamp) return false;
      const d = log.timestamp.toDate ? log.timestamp.toDate() : new Date(log.timestamp);
      return d.toISOString().slice(0, 10) === dateFilter;
    });
  }, [logs, dateFilter]);

  function handleExport() {
    const data = (filtered.length ? filtered : logs).map(l => ({
      id: l._docId, user: l.userName, action: l.action, module: l.module,
      time: formatTimestamp(l.timestamp),
    }));
    const filename = `audit-log-${new Date().toISOString().slice(0, 10)}.xml`;
    exportToXML(data, filename);
    setToast({ message: `Audit log exported as ${filename}`, type: 'success' });
  }

  return (
    <div className={styles.section}>
      <div className={styles.toolbar}>
        <div className={styles.filterGroup}>
          <label className={styles.filterLabel}>Date</label>
          <input type="date" className={styles.dateInput} value={dateFilter} onChange={e => setDateFilter(e.target.value)} />
          {dateFilter && <button className={styles.clearBtn} onClick={() => setDateFilter('')}>Clear</button>}
        </div>
        <button className={styles.exportBtn} onClick={handleExport}>Export Log</button>
      </div>

      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <thead>
            <tr><th>Log ID</th><th>User</th><th>Action</th><th>Module</th><th>Timestamp</th></tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={5} className={styles.empty}>{logs.length === 0 ? 'No audit logs yet.' : 'No logs for the selected date.'}</td></tr>
            ) : (
              filtered.map((log, i) => (
                <tr key={log._docId} className={i % 2 === 0 ? styles.rowEven : styles.rowOdd}>
                  <td className={styles.logId}>{log._docId}</td>
                  <td className={styles.logUser}>{log.userName}</td>
                  <td>{log.action}</td>
                  <td><ModuleBadge module={log.module} /></td>
                  <td className={styles.logTime}>{formatTimestamp(log.timestamp)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {toast && <ToastNotification message={toast.message} type={toast.type} onDismiss={dismissToast} />}
    </div>
  );
}
